import _ from "lodash";
import {getHiddenItemsFromGiveawayCampaigns, getUserTimeZone} from "../libraries/utils";
import appConfig from "../appconfig";
import MobileFriendlyCache from "../libraries/MobileFriendlyCache";
import OrderHelperObject from "../domains2/orders/OrderHelperObject";
import EmployeeTokenProvider from "../providers/EmployeeTokenProvider";
import routeMapper from "../router/routeMapper";
import * as mutationTypes from "../store/mutation-types";
import { mapGetters } from "vuex";
import AppError from "../domains2/libraries/AppError";
import SallingShopInfoProvider from "../providers/SallingShopInfoProvider";
import mutationTypesBM100 from "../store/globalTemplateSpecificModules/storeModulesBM100/mutationTypesBM100";
import { calculateNewShopWorkingHoursFields } from "../domains2/libraries/Shops";
import {SET_AB_TEST_HIDDEN_ITEMS, SET_DISCOUNT_CAPPED_STATE} from "../store/mutation-types";
import { sunsetGeolocationHelper } from "../helper/geolocation/sunsetGeolocationHelper";
import { defaultGeolocationHelper } from "../helper/geolocation/nonSunsetGeolocationHelper";
import geolocation from "../helper/geolocation/geolocation";
import { App as NativeApp } from "@capacitor/app";
import {store} from "../store";

const shopDataCache = new MobileFriendlyCache();

NativeApp.addListener("appStateChange", (state) => {
	if (state.isActive) {
		// Check for inactivity-based disposal first (mobile needs this for fire-and-forget scenarios)
		if (!shopDataCache.checkInactivityDisposal()) {
			// Only clean up expired items if cache wasn't disposed
			shopDataCache.cleanupExpired();
		}
	}
});

const TimeBox = appConfig.timeBoxImpl.timebox;
const localTimeHelperObj = appConfig.timeBoxImpl.helper("");
const tenantConfig = appConfig.tenantConfig;

export default {
	data() {
		return {
			loadingShops: true,
			loadingDistances: true,
			calculateShopTimings: true,
			sortByDistanceEnabled: false,
			searchString: "",
			pickupRules: [],
			allShops: [],
			shops: [],
			dialogs: {
				confirmationDialog: {
					confirmationOpened: false,
					onConfirm: null,
					params: [],
					header: "",
					text: "",
					nextConfirm: false,
					closeOnly: true
				}
			},
			sortingShops: false,
			triggerGetAndFillShopDistances: {
				flag: false,
				position: null
			}
		};
	},
	computed: {
		...mapGetters([
			"featureToggles",
			"cartProducts",
			"tenantSettings",
			"customerDetails",
			"favoriteRestaurants"
		]),
		loadedEnvironment() {
			return appConfig.loadedEnvironment();
		},
		isBM100() {
			return appConfig.isBM100();
		},
		tenantId() {
			return parseInt(
				window.localStorage.getItem("tenant") ||
				(this.customerDetails && this.customerDetails.tenantId) ||
				appConfig.tenantId
			);
		},
		callGetAndFillShopDistances() {
			const self = this;
			return self.allShops.length && self.triggerGetAndFillShopDistances.flag && self.triggerGetAndFillShopDistances.position;
		}
	},
	async created() {
		const self = this;
		this.$store.commit(SET_AB_TEST_HIDDEN_ITEMS, []);
		this.$store.commit(SET_DISCOUNT_CAPPED_STATE);
		if (self.isBM100) {
			self.loadingDistances = (await geolocation.checkPermissions()) === "granted";
		}
	},
	watch: {
		callGetAndFillShopDistances: {
			handler: function(newValue) {
				const self = this;
				if (newValue) {
					self.getAndFillShopDistances(self.triggerGetAndFillShopDistances.position);
					self.triggerGetAndFillShopDistances.flag = false;
					self.sortingShops = false;
				}
			}
		},
		searchString: _.debounce(function () {
			const self = this;
			const searchString = self.searchString ? self.searchString.trim() : "";
			const clonedShops = [...self.allShops];
			const applyFavoritesOrdering = (shops) => {
				if (self.featureToggles.favoriteRestaurants && self.favoriteRestaurants.length > 0) {
					const favorites = shops.filter(shop => shop.isFavorite);
					const others = shops.filter(shop => !shop.isFavorite);
					return [...favorites, ...others];
				}
				return shops;
			};

			if (!searchString.length) {
				if (self.sortByDistanceEnabled) {
					self.shops = self.sortShopsByDistance();
				} else {
					self.shops = applyFavoritesOrdering(self.sortShopsAlphabetically(clonedShops));
				}
			} else if (!isNaN(parseInt(searchString)) && ((searchString.length <= 4 && !self.isBM100) || (searchString.length < 4 && self.isBM100))) {
				const sortedShops = [...clonedShops].sort((first, second) => {
					// add zeros if needed
					const zipCodeLength = 4;
					const updatedZipCode = parseInt(searchString + (new Array(zipCodeLength - searchString.length)).fill("0").join(""));
					const proximityFirst = Math.abs(updatedZipCode - first.shopAddressObject.zip);
					const proximitySecond = Math.abs(updatedZipCode - second.shopAddressObject.zip);
					const proximitySort = proximityFirst - proximitySecond;

					if (proximitySort === 0) {
						// if zip code proximity is same then sort by character ascending
						return first.shopName.localeCompare(second.shopName);
					} else {
						return proximitySort;
					}
				});
				self.shops = applyFavoritesOrdering(sortedShops);
			} else {
				const searchStringWithoutSpecialChars = self.replaceSpecialChars(searchString);
				const filteredShops = clonedShops.filter(shop => {
					const shopName = shop.shopNameAlias !== null ? shop.shopNameAlias : shop.shopName;

					const searchConditions = [
						self.replaceSpecialChars(shopName),
						self.replaceSpecialChars(shop.shopAddressObject.street),
						self.replaceSpecialChars(shop.shopAddressObject.zip),
						self.replaceSpecialChars(shop.shopAddressObject.city)
					];

					return searchConditions.some(condition =>
						condition.indexOf(searchStringWithoutSpecialChars) > -1
					);
				});
				self.shops = applyFavoritesOrdering(filteredShops);
			}
		}, 10)
	},
	methods: {
		activateEmployeeDiscount(token) {
			this.$store.commit(mutationTypes.SET_EMPLOYEE_DISCOUNT_TOKEN, null);
			if (token) {
				EmployeeTokenProvider.activateEmployeeDiscountToken(token);
				this.$router.push({name: routeMapper.routeNames.shopSelection});
			}
		},
		remapShopData(shop) {
			const getOpeningTime = (shop, shopDay) => {
				if (!shopDataCache.get(`cacheShopOpeningTimes-${shop.id}`)) {
					// check if the current date is available
					const pickupRulesResult = OrderHelperObject.canBePickedUp({
						boxDate: TimeBox.now(),
						productSelections: this.cartProducts || [],
						tenantData: tenantConfig,
						pickupDateTimeRules: this.pickupRules,
						shopId: shop.id,
						productCatalog: this.$store.getters.productCatalog,
						hiddenItems: [store.getters.aBTestsHiddenItems, getHiddenItemsFromGiveawayCampaigns()].flat()
					});
					if (!pickupRulesResult) {
						shopDataCache.put(`cacheShopOpeningTimes-${shop.id}`, null);
					} else {
						shopDataCache.put(`cacheShopOpeningTimes-${shop.id}`, shopDay
							? (shopDay.opens || null)
							: OrderHelperObject.getEarliestPickupTime({
								boxDate: TimeBox.now(),
								products: this.cartProducts || [],
								tenantData: tenantConfig,
								pickupDateTimeRules: this.pickupRules,
								shopData: shop,
								includePastTimes: false,
								ignoreOffset: true,
								pickupTimeZone: shop.shopTimeZone
							}));
					}
				}

				const cacheResult = shopDataCache.get(`cacheShopOpeningTimes-${shop.id}`);
				return cacheResult ? cacheResult.value : null;
			};

			const getClosingTime = (shop, shopDay) => {
				if (!shopDataCache.get(`cacheShopClosingTimes-${shop.id}`)) {
					const latest = shopDay
						? (shopDay.closes || null)
						: OrderHelperObject.getLatestPickupTime({
							boxDate: TimeBox.now(),
							products: this.cartProducts,
							tenantData: tenantConfig,
							pickupDateTimeRules: this.pickupRules,
							shopData: shop,
							includePastTimes: false,
							ignoreOffset: true,
							pickupTimeZone: shop.shopTimeZone
						});
					shopDataCache.put(`cacheShopClosingTimes-${shop.id}`, (latest === "00:00" ? "24:00" : latest));
				}

				const cacheResult = shopDataCache.get(`cacheShopClosingTimes-${shop.id}`);
				return cacheResult ? cacheResult.value : null;
			};

			const shouldEnableShop = ({openingTime, closingTime, ...shop}) => {
				const closingTimeOffsetMinutes = () => {
					if (
						this.featureToggles &&
						this.featureToggles.useCustomClosingTimeOffset &&
						this.tenantSettings &&
						this.tenantSettings.customClosingOffsetTimeMinutes >= 0
					) {
						return this.tenantSettings.customClosingOffsetTimeMinutes;
					}
					return 10;
				};
				if (openingTime !== null && closingTime !== null) {
					const toCompareWithTime = localTimeHelperObj.relativeNowInTimeZone(getUserTimeZone(), shop.shopTimeZone);
					const beginningTime = TimeBox.fromTimeString(openingTime, shop.shopTimeZone);
					const endTime = TimeBox.fromTimeString(closingTime, shop.shopTimeZone);
					const endTimeWithOffset = endTime.subtract({minutes: closingTimeOffsetMinutes()});
					const nextOpeningTime = OrderHelperObject.getEarliestPickupTime({
						boxDate: endTime.add({minutes: 1}),
						products: this.cartProducts,
						tenantData: tenantConfig,
						pickupDateTimeRules: this.pickupRules,
						shopData: shop,
						includePastTimes: false,
						ignoreOffset: true,
						pickupTimeZone: shop.shopTimeZone
					});

					const openingClosingDifference = TimeBox
						.fromTimeString(nextOpeningTime, shop.shopTimeZone)
						.add({hours: 24})
						.diff(endTime)
						.exec();
					if (nextOpeningTime && beginningTime.format("HH:mm").exec() !== nextOpeningTime) {
						shopDataCache.put(`cacheShopMultipleWorkingTimes-${shop.id}`, endTime.toISO().exec());
					}
					const isShopWorkingMidnight = openingClosingDifference >= 0 && openingClosingDifference <= 5 * 60 * 1000;
					return isShopWorkingMidnight ||
						(toCompareWithTime.lt(endTimeWithOffset).exec() && toCompareWithTime.gt(beginningTime).exec());
				} else {
					return false;
				}
			};

			const getOpeningAndClosingTimeAndIsEnabled = (shop, shopDay) => {
				if (shopDataCache.get(`cacheShopMultipleWorkingTimes-${shop.id}`)) {
					const currentTime = localTimeHelperObj.relativeNowInTimeZone(getUserTimeZone(), shop.shopTimeZone);
					const cacheResult = shopDataCache.get(`cacheShopMultipleWorkingTimes-${shop.id}`);
					if (cacheResult && cacheResult.value) {
						const endOfCurrentWorkingTime = TimeBox.fromISO(cacheResult.value);
						if (currentTime.gt(endOfCurrentWorkingTime).exec()) {
							shopDataCache.remove(`cacheShopMultipleWorkingTimes-${shop.id}`);
							shopDataCache.remove(`cacheShopOpeningTimes-${shop.id}`);
							shopDataCache.remove(`cacheShopClosingTimes-${shop.id}`);
						}
					}
				}
				const openingTime = getOpeningTime(shop, shopDay);
				const closingTime = openingTime === null ? null : getClosingTime(shop, shopDay);
				return {
					openingTime,
					closingTime,
					enabled: shouldEnableShop({
						...shop,
						openingTime,
						closingTime
					})
				};
			};

			return {
				...shop,
				...getOpeningAndClosingTimeAndIsEnabled(shop)
			};
		},
		getShopTimingStatus(shop) {
			const _shopTimingConfiguration = (shop) => {
				const beginningTime = TimeBox.fromTimeString(shop.openingTime, shop.shopTimeZone);
				const endTime = TimeBox.fromTimeString(shop.closingTime, shop.shopTimeZone);
				return { beginningTime, endTime };
			};
			const toCompareWithTime = localTimeHelperObj.relativeNowInTimeZone(getUserTimeZone(), shop.shopTimeZone);
			const shopTimingConfiguration = _shopTimingConfiguration(shop);
			const aboutToOpen = toCompareWithTime.lt(shopTimingConfiguration.beginningTime).exec();
			const isClosed = toCompareWithTime.gt(shopTimingConfiguration.endTime).exec();
			const isOpen = aboutToOpen === false && isClosed === false;
			return {
				aboutToOpen,
				isClosed,
				isOpen
			};
		},
		loadHotjar(shop) {
			(function (h, o, t, j, a, r) {
				if(!window.GET_HOTJAR_ID || document.querySelector("#hotjarScript")) {
					return;
				}
				h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments); };
				h._hjSettings = { hjid: window.GET_HOTJAR_ID, hjsv: 6 };
				a = o.getElementsByTagName("head")[0];
				r = o.createElement("script"); r.async = 1;
				r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
				r.id = "hotjarScript";
				a.appendChild(r);
			})(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");
			window.hj("identify", null, { shop: shop });
		},
		isOpeningLaterToday(shop) {
			const toCompareWithTime = localTimeHelperObj.relativeNowInTimeZone(getUserTimeZone(), shop.shopTimeZone);
			const boxOpeningTime = TimeBox.fromTimeString(shop.openingTime, shop.shopTimeZone);
			if (shop.openingTime !== null) {
				return toCompareWithTime.lt(boxOpeningTime).exec();
			}
			return false;
		},
		openingTimeString(shop) {
			const toCompareWithTime = localTimeHelperObj.relativeNowInTimeZone(getUserTimeZone(), shop.shopTimeZone);

			if (shop.openingTime !== null && shop.closingTime !== null) {
				const beginningTime = TimeBox.fromTimeString(shop.openingTime, shop.shopTimeZone);
				const endTime = TimeBox.fromTimeString(shop.closingTime, shop.shopTimeZone);
				if (toCompareWithTime.lt(beginningTime).exec()) {
					return "Still not open";
				} else if (toCompareWithTime.gt(endTime).exec()) {
					return "Closed already";
				} else{
					return "Open now";
				}
			}
		},
		checkWorkingHours(shop) {
			return shop.openingTime !== null && shop.openingTime !== "" && shop.closingTime !== "";
		},
		parseDistance(shopDistance) {
			const self = this;

			if (self.isBM100) {
				if (shopDistance === null || shopDistance === undefined) {
					return "";
				} else if (parseFloat(shopDistance) > 50) {
					return self.$options.filters.capitalize(self.$t("common.moreThanKm", [50]));
				} else if (parseFloat(shopDistance) < 2) {
					const distanceString = `${shopDistance.toFixed(1)}`.replace(".", ",");
					return `${distanceString} km`;
				} else if (parseFloat(shopDistance) >= 2 && parseFloat(shopDistance) <= 50) {
					return `${Math.floor(shopDistance)} km`;
				} else {
					const distanceString = `${shopDistance.toFixed(2)}`.replace(".", ",");
					return `${distanceString} km`;
				}
			} else {
				if (shopDistance === Infinity || parseFloat(shopDistance) > 50) {
					return self.$options.filters.capitalize(self.$t("common.moreThanKm", [50]));
				} else if (shopDistance === 0) {
					return "0 m";
				} else if (shopDistance === -1) {
					return "";
				} else if (shopDistance <= 1 && shopDistance > 0) {
					const distance1 = (shopDistance * 100);
					const distance2 = (shopDistance * 100) % 100;
					if (distance2 > 50) {
						return `${(distance1 + 100 - distance2)} m`;
					} else {
						return `${(distance1 - distance2)} m`;
					}
				} else if (shopDistance > 1 && shopDistance <= 2) {
					const distance1 = (shopDistance * 1000);
					const distance2 = (shopDistance * 1000) % 100;
					if (distance2 > 50) {
						return `${(distance1 + 100 - distance2)} m`;
					} else {
						return `${(distance1 - distance2)} m`;
					}
				} else {
					if (shopDistance % 1 >= 0.5) {
						return `${Math.ceil(shopDistance)} km`;
					} else {
						return `${Math.floor(shopDistance)} km`;
					}
				}
			}
		},
		async selectShopSunset(shopId) {
			const self = this;
			const shop = self.shops.find(shop => shop.id === shopId);
			if (
				(shop.enabled || shop.isOpeningLaterToday) &&
				(!shop.temporarilyClosed || !shop.temporarilyClosedMessage)
			) {
				self.shopSelectedHandler(shop);
				const isConsentGivenThroughDataLayer = () => {
					const consents = (window.dataLayer || []).slice().reverse().find(function(item) {
						return item[0] === "consent";
					});
					const consentObject = consents && consents.length && consents[2];
					return consentObject && consentObject.analytics_storage === "granted";
				};
				if (
					(self.featureToggles.enableHotjarTracking || (shop && shop.shopOptions.hotjarTracking))
					&& (
						(window.CookieInformation && window.CookieInformation.getConsentGivenFor("cookie_cat_statistic"))
						|| isConsentGivenThroughDataLayer()
					)
				) {
					self.$nextTick(() => {
						self.loadHotjar(shop);
					});
				} else {
					document.querySelector("#hotjarScript") ? document.querySelector("#hotjarScript").remove() : null;
				}
			} else {
				self.setAlertDialog(shop);
				self.dialogs.confirmationDialog.confirmationOpened = true;
			}
		},
		setAlertDialog(shop) {
			const self = this;
			self.dialogs.confirmationDialog.confirmationOpened = true;
			self.dialogs.confirmationDialog.confirmOnly = true;
			// TODO: update function argument
			self.dialogs.confirmationDialog.text = shop.shopOptions.temporarilyClosedOptions.message;
			self.dialogs.confirmationDialog.header = shop.shopName;
			self.dialogs.confirmationDialog.params = shop;
			self.dialogs.confirmationDialog.onConfirm = async function () {
				self.dialogs.confirmationDialog.confirmationOpened = false;
			};
			self.dialogs.confirmationDialog.onClose = function () {
				self.dialogs.confirmationDialog.confirmationOpened = false;
			};
		},
		distanceInKmBetweenEarthCoordinates(lat1, lon1, lat2, lon2) {
			const degreesToRadians = (degrees) => {
				return degrees * Math.PI / 180;
			};
			const earthRadiusKm = 6371;
			const dLat = degreesToRadians(lat2-lat1);
			const dLon = degreesToRadians(lon2-lon1);

			lat1 = degreesToRadians(lat1);
			lat2 = degreesToRadians(lat2);

			const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
				Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
			const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
			return earthRadiusKm * c;
		},
		async getAndFillShopDistances(currentGeoPosition) {
			const self = this;
			try {
				const pos = currentGeoPosition;
				const clonedShops = [...self.allShops];
				const favoriteShops = self.featureToggles.favoriteRestaurants && self.favoriteRestaurants.length > 0 ? clonedShops.filter(shop => shop.isFavorite) : [];
				const otherShops = self.featureToggles.favoriteRestaurants && self.favoriteRestaurants.length > 0 ? clonedShops.filter(shop => !shop.isFavorite) : [];
				const shopsToSort = favoriteShops.length > 0 ? otherShops : clonedShops;
				if (self.isBM100) {
					const mappedShops = shopsToSort.map(shop => {
						if (!shop.distance && pos && pos.coords) {
							shop.distance = self.distanceInKmBetweenEarthCoordinates(
								pos.coords.latitude,
								pos.coords.longitude,
								shop.shopCoordinates[1],
								shop.shopCoordinates[0]
							);
						}
						return shop;
					});
					if (favoriteShops.length > 0) {
						self.shops = [...favoriteShops, ...mappedShops];
					} else {
						self.shops = mappedShops;
					}
				} else {
					const shopDistances = await (async() => {
						if (!pos) {
							return Promise.resolve({ data: [] });
						}
						return SallingShopInfoProvider.getShopsWithCoordinates("carlsjr", `${pos.coords.latitude},${pos.coords.longitude}`);
					})();

					self.shops = clonedShops.map(shop => {
						const shopDistance = shopDistances.data.find(distance => distance.id === shop.dsWebApiStoreId);
						shop.distance = shopDistance ? shopDistance.distance_km : Infinity;

						return shop;
					});
				}
				self.shops = self.sortShopsByDistance();
				self.sortByDistanceEnabled = true;
			} catch (e) {
				// eslint-disable-next-line no-console
				console.log(e);
				// self.shops = [...self.allShops];
			} finally {
				self.loadingDistances = false;
			}
		},
		sortShopsByDistance() {
			const self = this;
			const clonedShops = [...self.allShops];

			let normalDistances = [];
			let infinitelDistances = [];

			if (self.featureToggles.favoriteRestaurants && self.favoriteRestaurants.length > 0) {
				const favoriteShops = clonedShops.filter(shop => shop.isFavorite);
				const otherShops = clonedShops.filter(shop => !shop.isFavorite);
				normalDistances = otherShops.filter(shop => shop.distance !== Infinity).sort((first, second) => first.distance - second.distance);
				infinitelDistances = self.sortShopsAlphabetically(otherShops.filter(shop => shop.distance === Infinity));

				return [...favoriteShops, ...normalDistances, ...infinitelDistances];
			} else {
				normalDistances = clonedShops
					.filter(shop => shop.distance !== Infinity)
					.sort((first, second) => first.distance - second.distance);
				infinitelDistances = clonedShops
					.filter(shop => shop.distance === Infinity)
					.sort((first, second) => first.shopName.localeCompare(second.shopName));

				return [...normalDistances, ...infinitelDistances];
			}

		},
		moveFavoriteRestaurantsToTop() {
			const self = this;
			const favoriteRestaurants = self.shops.filter((shop) => shop.isFavorite);
			const otherShops = self.shops.filter((shop) => !shop.isFavorite);
			return [...favoriteRestaurants, ...otherShops];
		},
		resetLastCreatedOrderTime() {
			if (this.$store.getters.lastOrderCreatedTime !== null) {
				this.$store.commit(mutationTypesBM100.RESET_LAST_ORDER_CREATED_TIME);
			}
		},
		replaceSpecialChars(inputString) {
			const specialCharsReplace = [
				{
					search: "å",
					replace: "aa"
				}
			];

			return specialCharsReplace.reduce((carry, replaceRule) => {
				return carry.replace(replaceRule.search, replaceRule.replace);
			}, inputString.toLowerCase());
		},
		baseRemapShopData(shop, pickupRules) {
			const self = this;
			const shopData = {
				id: shop.id,
				shopName: shop.shopName,
				shopAddressObject: shop.shopAddress,
				shopAddress: `
					${shop.shopAddress.street},
					${shop.shopAddress.zip}
					${shop.shopAddress.city}`,
				shopStreet: shop.shopAddress.street,
				shopCityAndZip: `
					${shop.shopAddress.zip}
					${shop.shopAddress.city}
				`,
				shopOptions: shop.shopOptions,
				distance: shop.distance,
				dsWebApiStoreId: shop.dsWebApiStoreId,
				shopPhone: shop.shopPhone || shop.shopTelephoneNumber,
				shopWorkingHours: shop.shopWorkingHours,
				shopCoordinates: shop.shopCoordinates,
				shopNameAlias: shop.shopNameAlias,
				openingHoursByWeekDay: shop.openingHoursByWeekDay,
				deliveryMethods: shop.deliveryMethods,
				temporarilyClosed: shop.shopOptions.temporarilyClosedOptions.closed,
				temporarilyClosedMessage: shop.shopOptions.temporarilyClosedOptions.message,
				shopCountryCode: shop.shopAddress.country,
				shopTimeZone: shop.shopTimeZone,
				// TODO -> it might be that the merchantBillwerkAccountId is missing in the shops. For such cases, the BE will fallback
				// to the primary account but we don't have that for those shops here.
				// for such cases we will have "default" as the key. We should have this replaced with proper default account id.
				shopPaymentOptions: {
					...shop.shopPaymentOptions,
					merchantBillwerkAccountId: shop.shopPaymentOptions.merchantBillwerkAccountId || "default"
				},
				enabled: true
			};
			if (self.featureToggles.useNewShopsData) {
				// Use the relevant opening hours - as we could have moved out of the original opening hours
				Object.assign(shopData, calculateNewShopWorkingHoursFields({ pickupRules, shop, tenantData: tenantConfig }));
			}

			return shopData;
		},
		getCurrentLocation() {
			const self = this;
			if (self.isBM100) {
				return sunsetGeolocationHelper(self);
			} else {
				return defaultGeolocationHelper();
			}
		},
		sortShopsAlphabetically(shops) {
			const sorted = [...shops].sort((first, second) => first.shopName.localeCompare(second.shopName));

			return sorted;
		},
		async loadShops() {
			const self = this;
			self.getCurrentLocation();
			const getRequests = async () => {
				if (self.featureToggles.useNewShopsData) {
					return Promise.all([self.$store.dispatch(mutationTypes.FETCH_AND_CACHE_SHOPS), Promise.resolve([])]);
				}
				return Promise.all([
					self.$store.dispatch(mutationTypes.FETCH_AND_CACHE_SHOPS),
					self.$store.dispatch(mutationTypes.FETCH_AND_CACHE_PICKUP_RULES, {})
				]);
			};
			const [_shops, _pickupRules] = await getRequests();
			if (!self.featureToggles.useNewShopsData) {
				self.pickupRules = _pickupRules.filter((_pickupRule) => {
					return (
						_pickupRule.isActive &&
						!_pickupRule.productFilter &&
						_pickupRule.name !== "ShopManager_CloseProduct"
					);
				});
			}
			const baseRemappedShops = _shops
				.map((shop) => {
					const shopData = self.baseRemapShopData(shop, _pickupRules);
					if (self.featureToggles.favoriteRestaurants && self.favoriteRestaurants.length > 0) {
						shopData.isFavorite = self.favoriteRestaurants && self.favoriteRestaurants.some(
							(favRest) => favRest.shopId === shop.id && parseInt(favRest.tenantId) === self.tenantId
						);
					}
					return shopData;
				});

			if (self.featureToggles.favoriteRestaurants && self.favoriteRestaurants.length > 0) {
				const favoriteShops = [...baseRemappedShops].filter(shop => shop.isFavorite);
				const otherShops = [...baseRemappedShops].filter(shop => !shop.isFavorite);
				const sortedShops = self.sortShopsAlphabetically(otherShops);

				self.shops = [...favoriteShops, ...sortedShops];
			} else {
				self.shops = self.sortShopsAlphabetically(baseRemappedShops);
			}

			self.loadingShops = false;
			const getRemappedShopData = (shop) => {
				if (self.featureToggles.useNewShopsData) {
					return shop;
				}
				return self.remapShopData(shop);
			};

			self.shops = self.shops.map((shop) => {
				const shopData = getRemappedShopData(shop);
				if (!self.featureToggles.useNewShopsData) {
					shopData.isOpeningLaterToday = self.isOpeningLaterToday(shopData);
					shopData.openingTimeString = self.openingTimeString(shopData);
					shopData.checkWorkingHours = self.checkWorkingHours(shopData);
				}
				return shopData;
			});

			self.calculateShopTimings = false;

			self.allShops = [...self.shops];

			const wpShopNumber = self.$route.query.wpShopNumber;
			if (wpShopNumber) {
				const wpShopNumberExists = self.shops.find((shop) => shop.id === wpShopNumber);
				if (wpShopNumberExists) {
					self.selectShopSunset(wpShopNumber);
				} else {
					throw AppError.createNewError(`Shop with ID ${wpShopNumber} not found`);
				}
			}
		},
		isShopClosedWithDateRule(shop) {
			if (shop.openingHoursByWeekDay && shop.openingHoursByWeekDay.dateRules) {
				const now = new Date();
				const year = now.getFullYear();
				const month = now.getMonth()+1;
				const dayOfMonth = now.getDate();
				const item = shop.openingHoursByWeekDay.dateRules.find(x => {
					const [y, m, d] = x.date.split("-").map(item => parseInt(item));
					return year === y && month === m && dayOfMonth === d && x.closed;
				});
				return Boolean(item);
			}
		}
	}
};
